// Single source of truth for "has this capsule's run ended?".
//
// Sponsored / memberExclusive capsules are scheduled with a revealDate (start)
// and an endDate (stop). Once endDate passes the capsule must disappear
// everywhere: map markers, nearby lists, and the /capsule/:id detail page.
// Each read path used to re-implement `endDate?.toDate()`, so any path that
// forgot it leaked expired capsules — that is the bug this module closes.
//
// The coercion is deliberately permissive: endDate reaches us as a Firestore
// Timestamp from direct reads, but as a plain {seconds} / {_seconds} object or
// an ISO string when it has been through a callable's JSON response.

function toDateSafe(value) {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value?.toDate === "function") {
    try {
      const date = value.toDate();
      return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
    } catch {
      return null;
    }
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const seconds = Number(value?.seconds ?? value?._seconds);
  if (Number.isFinite(seconds)) {
    const date = new Date(seconds * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

/**
 * The capsule's end date as a Date, or null when it has none / it is unreadable.
 * A null result means "no scheduled end", i.e. the capsule runs indefinitely.
 */
export function toCapsuleEndDate(capsule) {
  return toDateSafe(capsule?.endDate);
}

/**
 * True once the capsule's scheduled end has been reached. Expiry is exact to
 * the millisecond — a capsule ending at 14:20 is gone at 14:20, not at midnight.
 */
export function isCapsuleEnded(capsule, nowMs = Date.now()) {
  const endDate = toCapsuleEndDate(capsule);
  if (!endDate) return false;
  return endDate.getTime() <= nowMs;
}
